import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { X, Fingerprint, Loader2, Check, Building2, Mail, Layers, Sparkles, ArrowRight } from "lucide-react";

const SRC_CLS = {
  "Custom-Build Inquiry": "bg-gold/15 text-gold border-gold/30",
  "Questionnaire": "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Mission Intelligence": "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "Business Memory": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};
const chip = (src) => src ? <span className={`text-[10px] px-2 py-0.5 border ${SRC_CLS[src] || "bg-white/5 text-gray-400 border-white/10"}`}>{src}</span> : null;

const levelOf = (s) => s >= 89 ? "Enterprise" : s >= 71 ? "Established" : s >= 52 ? "Growing" : s >= 25 ? "Startup" : "New lead";

// Operator view of a lead/client's Business DNA — "what do we already know?" with provenance.
export function BusinessDnaPanel({ email, clientId, projectId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bp, setBp] = useState(null);
  const [bpLoading, setBpLoading] = useState(false);

  const qs = () => {
    const q = new URLSearchParams();
    if (email) q.set("email", email);
    if (clientId) q.set("client_id", clientId);
    if (projectId) q.set("project_id", projectId);
    return q.toString();
  };
  const generateBlueprint = () => {
    setBpLoading(true);
    api.get(`/admin/growth-blueprint?${qs()}`).then((r) => setBp(r.data)).catch(() => {}).finally(() => setBpLoading(false));
  };

  useEffect(() => {
    const q = new URLSearchParams();
    if (email) q.set("email", email);
    if (clientId) q.set("client_id", clientId);
    if (projectId) q.set("project_id", projectId);
    setLoading(true);
    api.get(`/admin/business-dna?${q.toString()}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [email, clientId, projectId]);

  const fields = data?.fields || {};
  const dims = data?.knowledge?.dimensions || [];
  const score = data?.knowledge?.score ?? 0;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="business-dna-panel">
      <div className="absolute inset-0 bg-navy/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0b1327] border border-gold/25 shadow-2xl max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} data-testid="dna-panel-close" className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"><X size={20} /></button>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24"><Loader2 className="animate-spin text-gold" size={30} /><p className="text-gray-500 text-sm mt-3">Reading what we know…</p></div>
        ) : !data?.loaded ? (
          <div className="p-10 text-center">
            <Fingerprint size={40} className="text-gray-600 mx-auto" />
            <h3 className="font-heading text-xl text-white mt-4">No Business DNA yet</h3>
            <p className="text-gray-500 text-sm mt-2 max-w-sm mx-auto">We don't have enough signal on this business yet. It will fill in as they submit the questionnaire, run Mission Intelligence, or we create work for them.</p>
          </div>
        ) : (
          <div className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-11 h-11 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center text-gold"><Fingerprint size={20} /></div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-gold">Business DNA</div>
                <h3 className="font-heading text-2xl text-white leading-none mt-0.5">{data.company?.org_name || "Unknown business"}</h3>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400 mt-3 ml-14">
              {data.company?.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {data.company.email}</span>}
              {data.company?.industry && <span className="flex items-center gap-1.5"><Building2 size={13} /> {data.company.industry}</span>}
            </div>

            {/* Knowledge score */}
            <div className="bg-navy-800 border border-gold/20 p-5 mt-6">
              <div className="flex items-end justify-between mb-2">
                <div><div className="text-xs uppercase tracking-wider text-gold mb-1">Knowledge score</div><div className="font-heading text-4xl text-white" data-testid="dna-panel-score">{score}%</div></div>
                <div className="text-gold font-heading text-lg">{levelOf(score)}</div>
              </div>
              <div className="w-full bg-white/10 h-2.5 rounded-full overflow-hidden"><div className="h-full bg-gold transition-all duration-700" style={{ width: `${score}%` }} /></div>
            </div>

            {/* What we know (fields) */}
            <h4 className="font-heading text-lg text-white mt-6 mb-3">What we know so far</h4>
            <div className="space-y-2.5">
              {[["Industry", fields.industry], ["Primary goal", fields.goal], ["Audience", fields.audience]].map(([label, f]) => (
                <div key={label} className="flex items-start justify-between gap-3 bg-white/[0.03] border border-white/10 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
                    <div className={`text-sm mt-0.5 ${f?.value ? "text-gray-200" : "text-gray-600 italic"}`}>{f?.value || "Not known yet"}</div>
                  </div>
                  {chip(f?.source)}
                </div>
              ))}
            </div>

            {/* DNA strands */}
            <h4 className="font-heading text-lg text-white mt-6 mb-3 flex items-center gap-2"><Layers size={16} className="text-gold" /> DNA strands</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="dna-panel-strands">
              {dims.map((d) => (
                <div key={d.key} className={`flex items-center justify-between gap-2 border px-3 py-2.5 ${d.filled ? "border-emerald-500/25 bg-emerald-500/[0.05]" : "border-white/10"}`}>
                  <div className="flex items-center gap-2">
                    {d.filled ? <Check size={14} className="text-emerald-400" /> : <span className="w-3.5 h-3.5 rounded-full border border-white/20" />}
                    <span className={`text-sm ${d.filled ? "text-white" : "text-gray-500"}`}>{d.label}</span>
                  </div>
                  {chip(d.source)}
                </div>
              ))}
            </div>
            <div className="mt-7 pt-6 border-t border-white/10" data-testid="growth-blueprint">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h4 className="font-heading text-lg text-white flex items-center gap-2"><Sparkles size={16} className="text-gold" /> Growth Blueprint</h4>
                {!bp && (
                  <button onClick={generateBlueprint} disabled={bpLoading} data-testid="generate-blueprint-btn"
                    className="text-xs bg-gold text-navy px-3.5 py-2 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60 flex items-center gap-1.5">
                    {bpLoading ? <Loader2 className="animate-spin" size={13} /> : <>Generate <ArrowRight size={13} /></>}
                  </button>
                )}
              </div>
              {!bp ? (
                <p className="text-sm text-gray-500">Let Top Foster recommend the solutions this business needs — with the reasoning — for your review.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-300 mb-4">{bp.summary}</p>
                  {[[1, "Do now"], [2, "Up next"], [3, "Ongoing"]].map(([p, label]) => {
                    const items = (bp.solutions || []).filter((s) => s.priority === p);
                    if (!items.length) return null;
                    return (
                      <div key={p} className="mb-4">
                        <div className="text-[11px] uppercase tracking-wider text-gold mb-2">{label}</div>
                        <div className="space-y-2">
                          {items.map((s, i) => (
                            <div key={i} className="bg-white/[0.03] border border-white/10 p-3" data-testid={`blueprint-item-${p}-${i}`}>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm text-white font-medium">{s.name}</span>
                                <span className="text-[10px] text-gray-500 border border-white/10 px-2 py-0.5 shrink-0">{s.category}</span>
                              </div>
                              <p className="text-xs text-gray-400 mt-1 leading-relaxed">{s.why}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-xs text-gray-300 mt-1">AI-proposed for your review — nothing is sent or built automatically.</p>
                </>
              )}
            </div>

            <p className="text-xs text-gray-400 mt-5 leading-relaxed">This profile is assembled from everything we already know — it grows as this business submits the questionnaire, runs Mission Intelligence, and we create approved work. AIPOS reads it before drafting anything, so no one re-explains the business.</p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
